import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, Signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { FLOATING_PANEL_HEADER_ACTIONS, FloatingPanelHeaderAction } from '@shared/floating-panel/floating-panel-header-actions.token';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdGateInfoMode, HdGateInfoRequestedContext } from '../hd-gate-info-requested-context.model';
import { HdGateInfoSmallComponent } from './hd-gate-info-small.component';
import { HdGateInfoFullComponent } from './hd-gate-info-full.component';

/**
 * Router-component voor het floating gate-info panel (variant `chart-detail`
 * van de `hd-gate-info` factory). Bevat **geen** business-logic meer: het
 * kiest enkel welke sub-variant getoond wordt — `small` (compact) of `full`
 * (volledige detail-weergave) — op basis van een **lokale** mode-signal.
 *
 * De mode wordt geïnitialiseerd uit {@link HdGateInfoRequestedContext.initialMode}
 * (de app-default levert doorgaans `'full'`; ontbrekende context valt defensief
 * terug op `'small'`) en daarna lokaal geflipt door de toggle-knoppen van de
 * sub-varianten. Lokaal i.p.v. via een panel-ref zodat:
 *  - de content geen `FloatingPanelRef` hoeft te injecteren, en
 *  - een `replaceData(...)` (chart-refresh) de gekozen mode niet reset.
 */
@Component({
  selector: 'hd-gate-info-requested',
  templateUrl: './hd-gate-info-requested.component.html',
  styleUrl: './hd-gate-info-requested.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateInfoSmallComponent, HdGateInfoFullComponent],
})
export class HdGateInfoRequestedComponent {
  /**
   * Context kan op twee manieren worden aangeleverd:
   *  1. Als `[context]` input wanneer het component direct in een template
   *     wordt gebruikt (bv. via de `hd-gate-info` factory).
   *  2. Via het `FLOATING_PANEL_DATA` injection-token wanneer het component
   *     dynamisch in een floating panel wordt gemount door de
   *     {@link FloatingPanelService}.
   */
  readonly context = input<HdGateInfoRequestedContext | null>(null);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdGateInfoRequestedContext>
    | null;

  /**
   * Ref naar het eigen floating panel (enkel aanwezig in floating-modus, niet
   * wanneer de variant in een gedockte tab gemount is). Gebruikt om bij het
   * uitklappen naar `full` het — nu veel grotere — paneel te hercentreren zodat
   * het niet deels buiten beeld valt.
   */
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });

  /**
   * Dock-zone-grootte waarin deze variant gerenderd wordt. Enkel aanwezig
   * wanneer de content in een **gedockte** tab gemount is (aangeboden door de
   * dock-host). In de full-zone toont het panel steeds zijn volledige
   * detail-weergave, ongeacht de floating-variant waarmee het geopend werd.
   */
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });

  /**
   * Writable signaal voor de icoon-knoppen rechtsboven in de panel-host
   * (links van pin + sluit). Wordt enkel ingevuld wanneer het panel floating
   * is — in gedockte modus is er geen host-actions-strook en zou een toggle-
   * knop sowieso ondergeschikt zijn aan de dock-host-tabs.
   */
  private readonly headerActionsSignal = inject(FLOATING_PANEL_HEADER_ACTIONS, { optional: true });
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly effectiveContext = computed<HdGateInfoRequestedContext | null>(() => {
    return this.context() ?? this.panelData?.() ?? null;
  });

  /** Lokale inhouds-mode; geflipt door de toggle-knoppen van de sub-varianten. */
  protected readonly mode = signal<HdGateInfoMode>('small');
  private modeInitialized = false;

  /**
   * Tijdelijke A/B/C-keuze voor sectie-styling in de full dialog. Wordt aan
   * `<hd-gate-info-full>` doorgegeven en bestuurd via een cyclische toggle-
   * knop in de host-actions-strook. Default `'c'` — kandidaat voor de
   * uniforme sectietitel-stijl die straks over de hele app uitgerold wordt.
   *  - `'a'`: geen sectie-lijntjes (rustige look).
   *  - `'b'`: subtiele verticale stippel-lijntjes links van elke sectie.
   *  - `'c'` (default): amber horizontale lijn boven elke sectietitel +
   *    amber-gekleurde titel zelf.
   */
  protected readonly sectionVariant = signal<'a' | 'b' | 'c'>('c');

  protected cycleSectionVariant(): void {
    this.sectionVariant.update(v => v === 'a' ? 'b' : v === 'b' ? 'c' : 'a');
  }

  constructor() {
    // Eénmalige initialisatie van de mode uit de eerste beschikbare context.
    // Daarna blijft de gebruikerskeuze (toggle) leidend.
    effect(() => {
      const ctx = this.effectiveContext();
      if (this.modeInitialized || !ctx) return;
      this.modeInitialized = true;
      // Gedockt in de full-zone → forceer de volledige weergave, maar laat de
      // onderliggende `initialMode` ongemoeid zodat de restore-variant (small
      // vs. full) bij undocken bewaard blijft.
      this.mode.set(this.dockRenderZone === 'full' ? 'full' : (ctx.initialMode ?? 'small'));
    });

    // Floating-panel host-actions strook: links van pin/sluit verschijnen
    // (in lees-volgorde):
    //  1. A/B/C-cyclische toggle voor sectie-styling — enkel in full-mode
    //     (de small dialog heeft geen secties om te stylen).
    //  2. Compacte ↔ Volledige info-toggle (altijd).
    effect(() => {
      if (!this.headerActionsSignal) return;
      // Embedded gebruik ([context]-input, bv. de poort-weergave binnen het
      // kanaal-info paneel) is géén paneel-eigenaar: het geërfde header-
      // actions-signal hoort bij het HOST-paneel en moet met rust gelaten
      // worden (regressie 2026-07-03).
      if (this.context() != null) return;
      const lang = this.contentLang.effectiveLang();
      const mode = this.mode();
      const collapsing = mode === 'full';
      const modeAction: FloatingPanelHeaderAction = collapsing
        ? {
          icon: 'down-left-and-up-right-to-center',
          tooltip: this.transloco.translate('humanDesign.gateInfo.collapse', undefined, lang),
          onClick: () => this.showSmall(),
        }
        : {
          icon: 'up-right-and-down-left-from-center',
          tooltip: this.transloco.translate('humanDesign.gateInfo.expand', undefined, lang),
          onClick: () => this.showFull(),
        };

      const actions: FloatingPanelHeaderAction[] = [];
      if (mode === 'full') {
        const variant = this.sectionVariant();
        actions.push({
          label: variant.toUpperCase(),
          tooltip: `Sectie-styling: ${variant.toUpperCase()} (klik om te wisselen tussen A/B/C)`,
          onClick: () => this.cycleSectionVariant(),
        });
      }
      actions.push(modeAction);
      this.headerActionsSignal.set(actions);
    });
  }

  protected showFull(): void {
    this.mode.set('full');
    this.syncModeToData('full');
    // Het paneel wordt nu fors groter; hercentreer zodat het niet deels buiten
    // beeld valt (no-op wanneer gedockt of geen ref beschikbaar).
    this.panelRef?.recenter();
  }

  protected showSmall(): void {
    this.mode.set('small');
    this.syncModeToData('small');
  }

  /**
   * Schrijft de actieve toggle-keuze terug naar de panel-data als nieuwe
   * `initialMode`, zodat de gekozen variant (small/full) de restore-variant
   * wordt wanneer dit panel later (un)gedockt wordt. Enkel in floating-modus:
   * een gedockte tab heeft geen eigen `FloatingPanelRef`.
   */
  private syncModeToData(mode: HdGateInfoMode): void {
    // Embedded gebruik ([context]-input) is géén paneel-eigenaar: de geërfde
    // panelRef hoort bij het HOST-paneel en replaceData zou diens context
    // overschrijven — zo kreeg het kanaal-info paneel een poort-context en
    // crashte zijn sections-computed in een CD-loop (regressie 2026-07-03).
    if (this.context() != null) return;
    const ctx = this.effectiveContext();
    if (!this.panelRef || !ctx) return;
    this.panelRef.replaceData({ ...ctx, initialMode: mode });
  }
}
